/**
 * SystemStatus — live system health + operational signals, auto-refreshing.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Paper, Typography, Chip, CircularProgress, useTheme, alpha, Stack,
} from '@mui/material';
import { CheckCircle, Error as ErrorIcon, Refresh } from '@mui/icons-material';
import adminService from '../../services/adminService';
import { PageHeader, panelSx, ButtonPill } from '../ui';

const SystemStatus = () => {
  const theme = useTheme();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await adminService.getSystemHealth();
      setHealth(res || {});
      setUpdatedAt(new Date());
    } catch {
      setHealth({ error: true });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000); // live refresh every 15s
    return () => clearInterval(t);
  }, [load]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  const dbOk = health?.database?.connected === true || health?.database === true || health?.db === true;
  const counts = health?.counts || {};
  const signals = health?.pending_work || health?.signals || {};
  const env = health?.environment;
  const railsVersion = health?.versions?.rails || health?.rails_version;

  const countCards = [
    { label: 'Users', key: ['users'] },
    { label: 'Trips', key: ['trips'] },
    { label: 'Bookings', key: ['bookings'] },
    { label: 'Payments', key: ['payments'] },
  ];
  const signalCards = [
    { label: 'Unresolved contacts', key: ['unresolved_contacts', 'pending_contacts', 'contacts'] },
    { label: 'Pending bookings', key: ['pending_bookings', 'bookings'] },
    { label: 'Pending requests', key: ['pending_plan_requests', 'plan_requests'] },
  ];
  const pick = (obj, keys) => keys.map((k) => obj?.[k]).find((v) => v != null) ?? '—';

  return (
    <Box>
      <PageHeader
        title="System Status"
        subtitle="Live health and operational signals."
        action={<ButtonPill size="small" variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</ButtonPill>}
      />

      {/* Health banner */}
      <Paper elevation={0} sx={{ ...panelSx(theme), mb: 3, display: 'flex', alignItems: 'center', gap: 2, borderColor: dbOk ? alpha(theme.palette.success.main, 0.4) : alpha(theme.palette.error.main, 0.4) }}>
        {dbOk ? <CheckCircle sx={{ color: theme.palette.success.main, fontSize: 40 }} /> : <ErrorIcon sx={{ color: theme.palette.error.main, fontSize: 40 }} />}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{dbOk ? 'All systems operational' : 'Attention needed'}</Typography>
          <Typography variant="body2" color="text.secondary">
            Database {dbOk ? 'connected' : 'unreachable'}
            {env ? ` · ${env}` : ''}
            {railsVersion ? ` · Rails ${railsVersion}` : ''}
            {updatedAt ? ` · updated ${updatedAt.toLocaleTimeString()}` : ''}
          </Typography>
        </Box>
        <Chip label={dbOk ? 'Healthy' : 'Degraded'} color={dbOk ? 'success' : 'error'} sx={{ fontWeight: 700 }} />
      </Paper>

      <Typography variant="overline" color="text.secondary">Data volume</Typography>
      <Grid container spacing={2.5} sx={{ mt: 0, mb: 3 }}>
        {countCards.map((c) => (
          <Grid item xs={6} md={3} key={c.label}>
            <Paper elevation={0} sx={panelSx(theme)}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>{c.label}</Typography>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>{pick(counts, c.key)}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Typography variant="overline" color="text.secondary">Needs attention</Typography>
      <Grid container spacing={2.5} sx={{ mt: 0 }}>
        {signalCards.map((c) => {
          const v = pick(signals, c.key);
          const warn = typeof v === 'number' && v > 0;
          return (
            <Grid item xs={12} md={4} key={c.label}>
              <Paper elevation={0} sx={{ ...panelSx(theme), borderColor: warn ? alpha(theme.palette.warning.main, 0.5) : theme.palette.grey[200] }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>{c.label}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: warn ? theme.palette.warning.main : theme.palette.text.primary }}>{v}</Typography>
                </Stack>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default SystemStatus;
